import React, { useState, useRef, useEffect } from 'react';
import { CodeIcon, ChevronDownIcon, ChevronUpIcon } from '../icons';

const ContentTypeSelect = ({ 
  value, 
  onChange, 
  options = [], 
  className = "",
  placeholder = "Select type..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm) {
      setFilteredOptions(options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className={`relative ${className}`}>
      {/* Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center p-1 pr-3 h-[38px] rounded-xl hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A] transition-colors"
      >
        <div className="w-8 h-8 flex items-center justify-center">
          {selectedOption?.icon || <CodeIcon size={16} className="text-[#7B7B7B] dark:text-[#848484]" />}
        </div>
        <span className="text-sm mr-2 font-medium text-[#121212] dark:text-[#D0D0D0]">
          {selectedOption?.label || placeholder}
        </span>
        {isOpen ? (
          <ChevronUpIcon size={12} className="text-[#7B7B7B] dark:text-[#848484]" />
        ) : (
          <ChevronDownIcon size={12} className="text-[#7B7B7B] dark:text-[#848484]" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute bottom-full left-0 mb-1 w-[120px] bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020] rounded-lg shadow-lg z-50"
          style={{
            boxShadow: '0 -10px 21px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Options List */}
          <div className="p-2 space-y-1">
            {filteredOptions.map((option, index) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option)}
                className={`w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-[#F1F1F1] dark:hover:bg-[#252525] transition-colors ${
                  option.value === value ? 'bg-[#F1F1F1] dark:bg-[#252525]' : ''
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  {option.icon}
                </div>
                <span className="text-xs font-medium text-[#121212] dark:text-[#D0D0D0]">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentTypeSelect;
